import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../SectionHeading';
import { cardHover, fadeUp, slideInLeft, slideInRight, stagger, viewport } from '../motion';
import AchievementsImage from "../Logos/Achievements.png";
import ExhibitionImage from "../Logos/Exhibition.jfif";

// SVG Icons for Achievements
const TrophyIcon = () => (
  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0v6" />
  </svg>
);

const CodeIcon = () => (
  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const MedalIcon = () => (
  <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);

function Achievements() {
  const achievementsList = [
    {
      title: "1st Rank in Code-Menia",
      detail: "Secured 1st rank in the university-level Code-Menia programming competition, showcasing strong algorithmic problem-solving and coding speed.",
      icon: <TrophyIcon />,
      bgGradient: "from-amber-50 to-orange-100",
      borderColor: "border-amber-200"
    },
    {
      title: "1st Rank on GFG in University",
      detail: "Ranked #1 on GeeksforGeeks (GFG) platform among all university students, demonstrating consistent commitment to coding and learning.",
      icon: <StarIcon />,
      bgGradient: "from-yellow-50 to-yellow-100",
      borderColor: "border-yellow-200"
    },
    {
      title: "1st Prize in Project Exhibition",
      detail: "Awarded 1st prize in the university project exhibition for building innovative and high-impact software solutions.",
      icon: <MedalIcon />,
      bgGradient: "from-emerald-50 to-teal-100",
      borderColor: "border-emerald-200"
    },
    {
      title: "Solved 300+ Coding Questions",
      detail: "Solved over 300 competitive programming questions across multiple leading coding platforms (LeetCode, GFG, HackerRank).",
      icon: <CodeIcon />,
      bgGradient: "from-blue-50 to-indigo-100",
      borderColor: "border-blue-200"
    }
  ];

  return (
    <section id="achievements" className="py-20 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-20 left-10 w-40 h-40 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob-slow"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob-medium"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading eyebrow="Recognition" title="Achievements" className="mb-16">
          Recognitions and coding milestones achieved during my software development and academic
          journey.
        </SectionHeading>

        {/* Row 1: Media Cards (GeeksforGeeks Stats & Project Exhibition) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* GFG Profile Card */}
          <div>
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex flex-col h-full hover:shadow-2xl transition-shadow duration-300"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              whileHover={cardHover}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-center font-sans">GeeksforGeeks Coding Stats</h3>
              <div className="overflow-hidden rounded-xl border border-gray-200 shadow-inner bg-slate-900 p-2 flex justify-center items-center h-48 sm:h-56 md:h-64 w-full">
                <img 
                  src={AchievementsImage} 
                  alt="GeeksforGeeks Stats" 
                  className="max-w-full max-h-full object-contain rounded-lg hover:scale-105 transition duration-500"
                />
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Platform Rank</p>
                  <p className="text-lg font-bold text-indigo-700 font-sans">#1 in University</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium font-semibold font-sans">Coding Score</p>
                  <p className="text-lg font-bold text-emerald-600 font-sans">792 Points</p>
                </div>
                <a 
                  href="https://www.geeksforgeeks.org/profile/mayur_adlak?tab=activity" 
                  target="_blank" 
                  rel="noreferrer"
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white text-sm font-semibold rounded-lg shadow-md transition duration-300 hover:scale-105 text-center w-full sm:w-auto"
                >
                  View GFG Profile
                </a>
              </div>
            </motion.div>
          </div>

          {/* Project Exhibition Card */}
          <div>
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex flex-col h-full hover:shadow-2xl transition-shadow duration-300"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              whileHover={cardHover}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-center font-sans">Project Exhibition 1st Prize</h3>
              <div className="overflow-hidden rounded-xl border border-gray-200 shadow-inner bg-slate-900 p-2 flex justify-center items-center h-48 sm:h-56 md:h-64 w-full">
                <img 
                  src={ExhibitionImage} 
                  alt="Project Exhibition 1st Prize" 
                  className="max-w-full max-h-full object-cover rounded-lg hover:scale-105 transition duration-500 object-center"
                />
              </div>
              <p className="mt-4 text-sm text-gray-600 leading-relaxed text-center font-sans flex-grow">
                Awarded 1st Prize at Oriental University's project exhibition for building and demonstrating an innovative, high-impact software system.
              </p>
              <div className="mt-6 flex justify-center">
                <a 
                  href="https://www.linkedin.com/posts/mayur-adlak_project-share-team-activity-6999672842267054081-zClq?utm_source=social_share_send&utm_medium=member_desktop_web&rcm=ACoAADeroUcBgqSpkwF0fLuNeq_FzWcujrBOkkU" 
                  target="_blank" 
                  rel="noreferrer"
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-700 to-indigo-800 hover:from-blue-800 hover:to-indigo-900 text-white text-sm font-semibold rounded-lg shadow-md transition duration-300 hover:scale-105 text-center w-full sm:w-auto flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  View LinkedIn Post
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Row 2: Achievements Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          {achievementsList.map((achievement, index) => (
            <motion.div
              key={index}
              className={`bg-white p-6 rounded-2xl shadow-lg border ${achievement.borderColor} flex flex-col justify-between hover:shadow-2xl transition-shadow duration-300`}
              variants={fadeUp}
              whileHover={cardHover}
            >
              <div>
                <div className={`w-16 h-16 bg-gradient-to-br ${achievement.bgGradient} rounded-xl flex items-center justify-center mb-4 mx-auto md:mx-0`}>
                  {achievement.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2 text-center md:text-left">{achievement.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed text-center md:text-left">{achievement.detail}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Achievements;
